import React, { useState, useEffect, useRef } from "react";
import { loadStripe } from "@stripe/stripe-js";
import GenericModal from "../CommonComponents/genericModal/GenericModal";
import { useInitAchMutation } from "../../Apis/achApi";
import { enterpriseApiSlice } from "../../Apis";
import { useDispatch } from "react-redux";
import styles from "./addAchModal.module.css";
import { STRIPE_KEY } from "../../configs";

const stripePromise = loadStripe(STRIPE_KEY);

const SCREENS = {
  BANK_INFO: "BANK_INFO",
  MANDATE: "MANDATE",
  SUCCESS: "SUCCESS",
  ERROR: "ERROR",
};

const AddAchModal = ({ show, onClose }) => {
  const [currentScreen, setCurrentScreen] = useState(SCREENS.BANK_INFO);
  const dispatch = useDispatch();
  const [initAch, { data: initData, isLoading: isInitializing }] = useInitAchMutation();
  const clientSecret = initData?.clientSecret;

  // Reset to first screen and fetch client secret when modal opens
  useEffect(() => {
    if (show) {
      setCurrentScreen(SCREENS.BANK_INFO);
      initAch();
    }
  }, [show, initAch]);

  const handleClose = () => {
    setCurrentScreen(SCREENS.BANK_INFO);
    onClose();
  };

  const navigateToScreen = (screen) => {
    setCurrentScreen(screen);
  };

  const getModalTitle = () => {
    switch (currentScreen) {
      case SCREENS.BANK_INFO:
        return "Add Bank Information";
      case SCREENS.MANDATE:
        return "Confirmation";
      case SCREENS.SUCCESS:
      case SCREENS.ERROR:
        return "";
      default:
        return "Add Bank Information";
    }
  };

  const renderContent = () => {
    switch (currentScreen) {
      case SCREENS.BANK_INFO:
        return <BankInfoScreen onNext={() => navigateToScreen(SCREENS.MANDATE)} stripePromise={stripePromise} clientSecret={clientSecret} onError={() => navigateToScreen(SCREENS.ERROR)} isInitializing={isInitializing} />;
      case SCREENS.MANDATE:
        return <MandateScreen onSuccess={() => navigateToScreen(SCREENS.SUCCESS)} onError={() => navigateToScreen(SCREENS.ERROR)} onBack={() => navigateToScreen(SCREENS.BANK_INFO)} stripePromise={stripePromise} clientSecret={clientSecret} onClose={handleClose} dispatch={dispatch} />;
      case SCREENS.SUCCESS:
        return <SuccessScreen onClose={handleClose} />;
      case SCREENS.ERROR:
        return <ErrorScreen onClose={handleClose} />;
      default:
        return null;
    }
  };

  return (
    <GenericModal
      open={show}
      onClose={handleClose}
      backdropClassName={styles.modalBackdrop}
      modalClassName={styles.modalContent}
    >
      <div className={styles.modalInner}>
        {getModalTitle() && (
          <div className={styles.modalHeader}>
            <h3 className={styles.modalTitle}>{getModalTitle()}</h3>
            <button
              type="button"
              className={styles.closeButton}
              onClick={handleClose}
              aria-label="Close"
            >
              <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M10.6875 9.3125C11.0938 9.6875 11.0938 10.3438 10.6875 10.7188C10.5 10.9062 10.25 11 10 11C9.71875 11 9.46875 10.9062 9.28125 10.7188L6 7.4375L2.6875 10.7188C2.5 10.9062 2.25 11 2 11C1.71875 11 1.46875 10.9062 1.28125 10.7188C0.875 10.3438 0.875 9.6875 1.28125 9.3125L4.5625 6L1.28125 2.71875C0.875 2.34375 0.875 1.6875 1.28125 1.3125C1.65625 0.90625 2.3125 0.90625 2.6875 1.3125L6 4.59375L9.28125 1.3125C9.65625 0.90625 10.3125 0.90625 10.6875 1.3125C11.0938 1.6875 11.0938 2.34375 10.6875 2.71875L7.40625 6.03125L10.6875 9.3125Z"
                  fill="#242424"
                />
              </svg>
            </button>
          </div>
        )}
        <div className={styles.modalBody}>
          {renderContent()}
        </div>
      </div>
    </GenericModal>
  );
};

// Bank Info Screen Component
const BankInfoScreen = ({ onNext, stripePromise, clientSecret, onError, isInitializing }) => {
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    
    if (!clientSecret) {
      console.error("Client secret not available");
      onError();
      return;
    }

    setIsSubmitting(true);
    const name = nameRef.current.value;
    const email = emailRef.current.value;

    try {
      const stripe = await stripePromise;
      
      const { setupIntent, error } = await stripe.collectBankAccountForSetup({
        clientSecret: clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: {
              name: name,
              email: email,
            },
          },
        },
        expand: ['payment_method'],
      });

      if (error) {
        console.error(error.message);
        onError();
      } else if (setupIntent.status === 'requires_payment_method') {
        // Customer canceled the hosted verification modal
        setIsSubmitting(false);
      } else if (setupIntent.status === 'requires_confirmation') {
        // We collected an account - display mandate confirmation
        ev.target.reset();
        onNext();
      }
    } catch (error) {
      console.error("Error collecting bank account:", error);
      onError();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.screen}>
      <form onSubmit={handleSubmit}>
        <div className={styles.bankInput}>
          <label className={styles.formLabel}>Account Holder Name</label>
          <input
            ref={nameRef}
            type="text"
            className={styles.formControl}
            placeholder="John Doe"
            required
          />
        </div>
        <div className={styles.bankInput}>
          <label className={styles.formLabel}>Email Address</label>
          <input
            ref={emailRef}
            type="email"
            className={styles.formControl}
            placeholder="john@gmail.com"
            required
          />
        </div>
        <button type="submit" className={styles.submitBtn} disabled={isSubmitting || isInitializing || !clientSecret}>
          {isInitializing ? "Initializing..." : isSubmitting ? "Processing..." : "Proceed to next step"}
        </button>
      </form>
    </div>
  );
};

// Mandate Confirmation Screen Component
const MandateScreen = ({ onSuccess, onError, onBack, stripePromise, clientSecret, onClose, dispatch }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAccept = async (ev) => {
    ev.preventDefault();
    
    if (!clientSecret) {
      console.error("Client secret not available");
      onError();
      return;
    }

    setIsSubmitting(true);

    try {
      const stripe = await stripePromise;
      
      const { setupIntent, error } = await stripe.confirmUsBankAccountSetup(clientSecret);

      if (error) {
        console.error(error.message);
        onError();
      } else if (setupIntent.status === "requires_payment_method") {
        onError();
      } else if (setupIntent.status === "succeeded") {
        // Invalidate ACH tag to trigger refetch after delay to allow backend to populate data
        setTimeout(() => {
          dispatch(enterpriseApiSlice.util.invalidateTags(["Ach"]));
        }, 2000);
        onSuccess();
      } else if (setupIntent.next_action?.type === "verify_with_microdeposits") {
        // The account needs to be verified via microdeposits
        // Display a message to consumer with next steps
        console.log("Account requires microdeposit verification");
        onSuccess(); // For now, treat as success
      }
    } catch (error) {
      console.error("Error confirming bank account:", error);
      onError();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.screen}>
      <form onSubmit={handleAccept}>
        <div className={styles.bankMandateText}>
          By confirming this mandate, you authorize Shipday to initiate debits from the bank account specified
          above for any amount owed for charges arising from your use of Shipday's services and/or purchase of products,
          in accordance with Shipday's website and terms. This authorization will remain in effect until revoked. You
          may amend or cancel this authorization at any time by providing Shipday with a 30-day notice.
          <br/><br/>
          If you use Shipday's services or make periodic purchases as per Shipday's terms, you authorize Shipday
          to periodically debit your bank account. Payments that fall outside the regular authorized debits will
          be processed only after obtaining your specific authorization.
          <br/><br/>
          Please review the above authorization carefully. By proceeding with the ACH payment, you confirm that
          you have read, understood, and agreed to the terms and conditions outlined in this mandate modal.
          <br/><br/>
          If you have any questions or need further assistance, please contact Shipday's <a href="mailto:support@shipday.com">customer support</a>.
        </div>
        <div className={styles.mandateButtons}>
          <button type="button" className={styles.cancelBtn} onClick={onBack} disabled={isSubmitting}>
            Decline
          </button>
          <button type="submit" className={styles.submitBtn} disabled={isSubmitting}>
            {isSubmitting ? "Processing..." : "Accept"}
          </button>
        </div>
      </form>
    </div>
  );
};

// Success Screen Component
const SuccessScreen = ({ onClose }) => {
  return (
    <div className={styles.successScreen}>
      <div className={styles.successIcon}>
        <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="64" height="64" rx="32" fill="#17C289" fillOpacity="0.11"/>
          <path d="M42.6666 24L28 38.6667L21.3333 32" stroke="#17C289" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <div className={styles.successTitle}>Bank Information Successfully Added</div>
      <button className={styles.successBtn} onClick={onClose}>Done</button>
    </div>
  );
};

// Error Screen Component
const ErrorScreen = ({ onClose }) => {
  return (
    <div className={styles.errorScreen}>
      <div className={styles.errorTitle}>Ran into an error.</div>
      <div className={styles.errorText}>
        Please check with your Stripe account, if any information was wrong and try again. If the issue persists, please contact support for further help.
      </div>
      <button className={styles.submitBtn} onClick={onClose}>Close</button>
    </div>
  );
};

export default AddAchModal;
